import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Row, Col, Descriptions, Tag, Typography, Button, Skeleton, Statistic,
  Table, Empty, Space, Alert, Avatar, Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined, ReloadOutlined, UserOutlined,
  FacebookFilled, InstagramFilled, CheckCircleFilled, CloseCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { adminApi } from '../services/api';

const { Title, Text } = Typography;

const STATO: Record<string, { t: string; c: string }> = {
  active:    { t: 'Attivo',      c: 'success' },
  trialing:  { t: 'In prova',    c: 'processing' },
  trial:     { t: 'In prova',    c: 'processing' },
  paused:    { t: 'In pausa',    c: 'warning' },
  canceling: { t: 'In disdetta', c: 'warning' },
  past_due:  { t: 'Non pagato',  c: 'error' },
  canceled:  { t: 'Chiuso',      c: 'default' },
  inactive:  { t: 'Inattivo',    c: 'default' },
};

const STATO_POST: Record<string, { t: string; c: string }> = {
  published:  { t: 'Pubblicato', c: 'success' },
  failed:     { t: 'Fallito',    c: 'error' },
  publishing: { t: 'In corso',   c: 'processing' },
  draft:      { t: 'Bozza',      c: 'default' },
};

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setErrore('');
    try {
      const res = await adminApi.userDetail(parseInt(id, 10));
      setData(res.data);
    } catch {
      setErrore('Utente non trovato o non raggiungibile.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <>
        <Skeleton active paragraph={{ rows: 1 }} style={{ maxWidth: 320, marginBottom: 20 }} />
        <Row gutter={[16, 16]}>
          {[0, 1, 2, 3].map(i => (
            <Col xs={24} md={12} key={i}><Card><Skeleton active paragraph={{ rows: 3 }} /></Card></Col>
          ))}
        </Row>
      </>
    );
  }

  if (errore || !data) {
    return (
      <Alert
        type="error" showIcon
        message="Utente non disponibile" description={errore}
        action={
          <Space>
            <Button size="small" onClick={load}>Riprova</Button>
            <Button size="small" type="primary" onClick={() => navigate('/users')}>Torna all'elenco</Button>
          </Space>
        }
      />
    );
  }

  const { user, stats } = data;
  const s = STATO[user.subscription_status] || { t: user.subscription_status || '—', c: 'default' };

  const si = (v: any) => v
    ? <CheckCircleFilled style={{ color: '#0EA26B' }} />
    : <CloseCircleOutlined style={{ color: '#98A1B8' }} />;

  const colonne: ColumnsType<any> = [
    {
      title: 'Contenuto', dataIndex: 'caption',
      render: c => c
        ? <Text style={{ maxWidth: 360 }} ellipsis={{ tooltip: c }}>{c}</Text>
        : <Text type="secondary">senza testo</Text>,
    },
    {
      title: 'Stato', dataIndex: 'status', width: 130,
      render: (v: string) => {
        const x = STATO_POST[v] || { t: v, c: 'default' };
        return <Tag color={x.c}>{x.t}</Tag>;
      },
    },
    {
      title: 'Facebook', dataIndex: 'facebook_post_id', width: 100, align: 'center',
      responsive: ['md'], render: si,
    },
    {
      title: 'Instagram', dataIndex: 'instagram_post_id', width: 100, align: 'center',
      responsive: ['md'], render: si,
    },
    {
      title: 'Data', dataIndex: 'created_at', width: 115, responsive: ['lg'],
      render: (d: string) => new Date(d).toLocaleDateString('it-IT'),
    },
  ];

  return (
    <>
      <Button
        type="text" icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/users')}
        style={{ marginBottom: 12, paddingLeft: 0 }}
      >
        Tutti gli utenti
      </Button>

      <div className="page-top">
        <Space size={14} align="start">
          <Avatar size={46} style={{ background: '#4F72FF', flex: '0 0 auto' }} icon={<UserOutlined />} />
          <div>
            <Title level={3} style={{ margin: 0 }}>{user.name || '—'}</Title>
            <Space size={8} wrap>
              <Text type="secondary" copyable>{user.email}</Text>
              <Tag color={s.c}>{s.t}</Tag>
            </Space>
          </div>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={load}>Aggiorna</Button>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={8}><Card size="small"><Statistic title="Post totali" value={stats.total_posts} /></Card></Col>
        <Col xs={8}>
          <Card size="small">
            <Statistic title="Pubblicati" value={stats.published_posts} valueStyle={{ color: '#0EA26B' }} />
          </Card>
        </Col>
        <Col xs={8}>
          <Card size="small">
            <Statistic title="Falliti" value={stats.failed_posts}
                       valueStyle={stats.failed_posts > 0 ? { color: '#DC3055' } : undefined} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="Attività">
            <Descriptions column={1} size="small" colon={false}>
              <Descriptions.Item label="Nome">{user.business_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Indirizzo">{user.business_address || '—'}</Descriptions.Item>
              <Descriptions.Item label="Telefono">{user.phone || '—'}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Abbonamento">
            <Descriptions column={1} size="small" colon={false}>
              <Descriptions.Item label="Piano">
                {user.subscription_plan
                  ? <Tag color="blue">{user.subscription_plan}</Tag>
                  : <Text type="secondary">nessuno</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Stato"><Tag color={s.c}>{s.t}</Tag></Descriptions.Item>
              <Descriptions.Item label="Cliente Stripe">
                {user.stripe_id
                  ? <Text copyable style={{ fontSize: 12.5 }}>{user.stripe_id}</Text>
                  : <Text type="secondary">—</Text>}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Social collegati">
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              <Space>
                <FacebookFilled style={{ color: user.facebook_page_name ? '#1877F2' : '#C7CDDB', fontSize: 19 }} />
                {user.facebook_page_name
                  ? <Text strong>{user.facebook_page_name}</Text>
                  : <Text type="secondary">Non collegato</Text>}
              </Space>
              <Space>
                <InstagramFilled style={{ color: user.instagram_username ? '#C13584' : '#C7CDDB', fontSize: 19 }} />
                {user.instagram_username
                  ? <Text strong>@{user.instagram_username}</Text>
                  : <Text type="secondary">Non collegato</Text>}
              </Space>
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <Tooltip title="Identificativi interni delle promozioni attive su Meta">
                <span>Promozioni</span>
              </Tooltip>
            }
          >
            <Descriptions column={1} size="small" colon={false}>
              <Descriptions.Item label="Copertura">
                {user.campaign_reach_id
                  ? <Text copyable style={{ fontSize: 12.5 }}>{user.campaign_reach_id}</Text>
                  : <Text type="secondary">nessuna</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Interazione">
                {user.campaign_interaction_id
                  ? <Text copyable style={{ fontSize: 12.5 }}>{user.campaign_interaction_id}</Text>
                  : <Text type="secondary">nessuna</Text>}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      <Card title="Ultimi post" styles={{ body: { padding: 0 } }}>
        <Table
          rowKey="id"
          columns={colonne}
          dataSource={user.posts || []}
          pagination={false}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Questo utente non ha ancora pubblicato" />,
          }}
          scroll={{ x: 620 }}
        />
      </Card>
    </>
  );
}
